import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { 
  Users, Plus, Mail, Phone, User, DollarSign,
  Shield, CheckCircle, XCircle, Search, KeyRound, Lock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { UserAvatar } from '../components/UserAvatar';
import { toast } from 'sonner';

const LEVEL_STYLES = {
  executive: 'bg-[#D4AF37]/20 text-[#D4AF37]',
  senior_management: 'bg-[#4B167A]/50 text-white',
  management: 'bg-[#1F1B71]/70 text-white',
  specialist: 'bg-sky-500/20 text-sky-300',
  supervisory: 'bg-amber-500/20 text-amber-300',
  agent: 'bg-white/10 text-white/80',
  client: 'bg-[#10B981]/20 text-[#10B981]'
};

const CLIENT_OPTION = '__client__';

export default function StaffManagementPage() {
  const { api, user } = useAuth();
  const perms = user?.permissions || {};
  const [users, setUsers] = useState([]);
  const [titles, setTitles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone_number: '',
    title: 'Customer Service Representative / Agent',
    contract_rate: 15.0
  });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const [u, t] = await Promise.all([api.get('/admin/users'), api.get('/org/titles')]);
      setUsers(u.data);
      setTitles(t.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'contract_rate' ? parseFloat(value) || 0 : value
    }));
  };

  const handleAddUser = async () => {
    if (!formData.email || !formData.password || !formData.full_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const isClient = formData.title === CLIENT_OPTION;
      await api.post('/auth/register', {
        ...formData,
        role: isClient ? 'client' : 'agent',
        title: isClient ? null : formData.title
      });
      toast.success(`${formData.full_name} has been added successfully. They can now login and set up MFA.`);
      setShowAddModal(false);
      setFormData({
        email: '',
        password: '',
        full_name: '',
        phone_number: '',
        title: 'Customer Service Representative / Agent',
        contract_rate: 15.0
      });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleToggleActive = async (userId, currentStatus) => {
    try {
      if (currentStatus) {
        await api.delete(`/admin/users/${userId}`);
        toast.success('User deactivated');
      } else {
        await api.put(`/admin/users/${userId}`, { is_active: true });
        toast.success('User activated');
      }
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleUpdateTitle = async (userId, newTitle) => {
    try {
      await api.put(`/org/users/${userId}/title`, { title: newTitle });
      toast.success('Title and access level updated');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update title');
    }
  };

  const handleResetPassword = async () => {
    if (!resetPassword || resetPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    try {
      await api.post(`/org/users/${resetTarget.id}/reset-password`, { new_password: resetPassword });
      toast.success(`Password reset for ${resetTarget.full_name}`);
      setResetTarget(null);
      setResetPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const levelOf = (u) => u.permissions?.access_level || (u.role === 'client' ? 'client' : 'agent');

  const filteredUsers = users.filter(u => {
    if (levelFilter !== 'all' && levelOf(u) !== levelFilter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return u.full_name?.toLowerCase().includes(query) || 
             u.email?.toLowerCase().includes(query) ||
             u.title?.toLowerCase().includes(query);
    }
    return true;
  });

  const stats = {
    total: users.length,
    active: users.filter(u => u.is_active !== false).length,
    agents: users.filter(u => levelOf(u) === 'agent').length,
    managers: users.filter(u => !['agent', 'client'].includes(levelOf(u))).length
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="staff-management-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              Staff <span className="text-[#D4AF37]">Management</span>
            </h1>
            <p className="text-white/50 mt-1">{perms.can_add_users ? 'Provision accounts and manage the 23-tier hierarchy' : 'Password resets and credential support'}</p>
          </div>
          {perms.can_add_users ? (
            <Button
              onClick={() => setShowAddModal(true)}
              className="bg-[#D4AF37] text-[#0A091E] font-bold hover:shadow-[0_0_25px_rgba(212,175,55,0.6)]"
              data-testid="btn-add-user"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Staff Member
            </Button>
          ) : (
            <Badge className="bg-amber-500/20 text-amber-300 border border-amber-500/30 py-2 px-3" data-testid="provisioning-locked-badge">
              <Lock className="w-3 h-3 mr-2" /> Account provisioning restricted to Project Manager level and above
            </Badge>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="glass-card border-0">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-white/50 text-xs uppercase">Total Staff</p>
                  <p className="text-2xl font-bold text-white">{stats.total}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card border-0">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-[#10B981]" />
                </div>
                <div>
                  <p className="text-white/50 text-xs uppercase">Active</p>
                  <p className="text-2xl font-bold text-white">{stats.active}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card border-0">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#1F1B71]/60 flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-white/50 text-xs uppercase">Agents</p>
                  <p className="text-2xl font-bold text-white">{stats.agents}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card border-0">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#4B167A]/40 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-white/50 text-xs uppercase">Leadership & Staff</p>
                  <p className="text-2xl font-bold text-white">{stats.managers}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="glass-card border-0">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-[#0A091E]/50 border-white/20 text-white placeholder:text-white/30"
                  data-testid="input-search"
                />
              </div>
              <Select value={levelFilter} onValueChange={setLevelFilter}>
                <SelectTrigger className="w-full md:w-52 bg-[#0A091E]/50 border-white/20 text-white" data-testid="select-level-filter">
                  <SelectValue placeholder="Filter by level" />
                </SelectTrigger>
                <SelectContent className="bg-[#1F1B71] border-white/10 text-white">
                  <SelectItem value="all">All Access Levels</SelectItem>
                  <SelectItem value="executive">Executive</SelectItem>
                  <SelectItem value="senior_management">Senior Management</SelectItem>
                  <SelectItem value="management">Management</SelectItem>
                  <SelectItem value="specialist">Specialist</SelectItem>
                  <SelectItem value="supervisory">Supervisory</SelectItem>
                  <SelectItem value="agent">Agent</SelectItem>
                  <SelectItem value="client">Client</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Users List */}
        <Card className="glass-card border-0">
          <CardHeader className="border-b border-white/10">
            <CardTitle className="flex items-center gap-2 text-white">
              <Users className="w-5 h-5 text-[#D4AF37]" />
              Staff Directory
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-white/50">Loading...</div>
            ) : filteredUsers.length === 0 ? (
              <div className="p-8 text-center text-white/50">
                {searchQuery || levelFilter !== 'all' ? 'No users match your filters' : 'No staff members yet'}
              </div>
            ) : (
              <div className="divide-y divide-white/10">
                {filteredUsers.map((staffUser) => (
                  <div 
                    key={staffUser.id} 
                    className={`p-4 flex flex-col md:flex-row md:items-center justify-between gap-4 ${staffUser.is_active === false ? 'opacity-50' : ''}`}
                    data-testid={`user-row-${staffUser.id}`}
                  >
                    <div className="flex items-center gap-4 min-w-0">
                      <UserAvatar user={staffUser} size="w-12 h-12" />
                      <div className="min-w-0">
                        <p className="font-semibold text-white truncate">{staffUser.full_name}</p>
                        <p className="text-white/50 text-sm truncate">{staffUser.email}</p>
                        <p className="text-[#D4AF37]/80 text-xs truncate" data-testid={`user-title-${staffUser.id}`}>{staffUser.title || 'Client Account'}</p>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 md:gap-4">
                      <Badge className={LEVEL_STYLES[levelOf(staffUser)]}>
                        {staffUser.permissions?.access_level_label || 'Client'}
                      </Badge>
                      
                      {staffUser.mfa_enabled && (
                        <Badge className="bg-[#10B981]/20 text-[#10B981]">
                          <Shield className="w-3 h-3 mr-1" />
                          MFA
                        </Badge>
                      )}

                      <span className="text-white/50 text-sm font-mono">
                        ${staffUser.contract_rate}/hr
                      </span>

                      {staffUser.id !== user?.id && (
                        <div className="flex items-center gap-2">
                          {perms.can_add_users && staffUser.role !== 'client' && (
                            <Select
                              value={staffUser.title || ''}
                              onValueChange={(value) => handleUpdateTitle(staffUser.id, value)}
                            >
                              <SelectTrigger className="w-44 h-8 bg-[#0A091E]/50 border-white/20 text-white text-xs" data-testid={`select-title-${staffUser.id}`}>
                                <SelectValue placeholder="Set title" />
                              </SelectTrigger>
                              <SelectContent className="bg-[#1F1B71] border-white/10 text-white max-h-72">
                                {titles.map(t => (
                                  <SelectItem key={t.tier} value={t.title} className="text-xs">{t.tier}. {t.title}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}

                          {perms.can_reset_passwords && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setResetTarget(staffUser)}
                              className="text-white/60 hover:text-[#D4AF37]"
                              title="Reset password"
                              data-testid={`btn-reset-password-${staffUser.id}`}
                            >
                              <KeyRound className="w-4 h-4" />
                            </Button>
                          )}

                          {perms.can_add_users && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleToggleActive(staffUser.id, staffUser.is_active !== false)}
                              className={staffUser.is_active === false ? 'text-[#10B981] hover:text-[#10B981]/80' : 'text-[#EF4444] hover:text-[#EF4444]/80'}
                              data-testid={`btn-toggle-active-${staffUser.id}`}
                            >
                              {staffUser.is_active === false ? (
                                <CheckCircle className="w-4 h-4" />
                              ) : (
                                <XCircle className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Password Reset Modal */}
      <Dialog open={!!resetTarget} onOpenChange={(o) => { if (!o) { setResetTarget(null); setResetPassword(''); } }}>
        <DialogContent className="bg-[#1F1B71] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-[#D4AF37]" />
              Reset Password — {resetTarget?.full_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-4">
            <Label className="text-white/70">New Temporary Password</Label>
            <Input
              type="password"
              value={resetPassword}
              onChange={(e) => setResetPassword(e.target.value)}
              placeholder="Minimum 8 characters"
              className="bg-[#0A091E]/50 border-white/20 text-white placeholder:text-white/30"
              data-testid="input-reset-password"
            />
            <p className="text-white/40 text-xs">The team member should change this password after logging in. This action is recorded in the audit trail.</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setResetTarget(null)} className="text-white/70">Cancel</Button>
            <Button onClick={handleResetPassword} className="bg-[#D4AF37] text-[#0A091E]" data-testid="btn-confirm-reset-password">Reset Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add User Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-[#1F1B71] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-[#D4AF37]" />
              Add New Staff Member
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label className="text-white/70">Full Name *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <Input
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleInputChange}
                    placeholder="John Smith"
                    className="pl-10 bg-[#0A091E]/50 border-white/20 text-white placeholder:text-white/30"
                    data-testid="input-fullname"
                  />
                </div>
              </div>

              <div className="col-span-2 space-y-2">
                <Label className="text-white/70">Corporate Email *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="john@srm-cadence.com"
                    className="pl-10 bg-[#0A091E]/50 border-white/20 text-white placeholder:text-white/30"
                    data-testid="input-email"
                  />
                </div>
              </div>

              <div className="col-span-2 space-y-2">
                <Label className="text-white/70">Temporary Password *</Label>
                <Input
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Initial password for first login"
                  className="bg-[#0A091E]/50 border-white/20 text-white placeholder:text-white/30"
                  data-testid="input-password"
                />
                <p className="text-white/40 text-xs">User will set up MFA on first login</p>
              </div>

              <div className="space-y-2">
                <Label className="text-white/70">Phone Number</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <Input
                    name="phone_number"
                    type="tel"
                    value={formData.phone_number}
                    onChange={handleInputChange}
                    placeholder="+1 (555) 000-0000"
                    className="pl-10 bg-[#0A091E]/50 border-white/20 text-white placeholder:text-white/30"
                    data-testid="input-phone"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-white/70">Contract Rate ($/hr)</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <Input
                    name="contract_rate"
                    type="number"
                    step="0.50"
                    value={formData.contract_rate}
                    onChange={handleInputChange}
                    className="pl-10 bg-[#0A091E]/50 border-white/20 text-white"
                    data-testid="input-rate"
                  />
                </div>
              </div>

              <div className="col-span-2 space-y-2">
                <Label className="text-white/70">Organizational Title *</Label>
                <Select
                  value={formData.title}
                  onValueChange={(value) => setFormData({ ...formData, title: value })}
                >
                  <SelectTrigger className="bg-[#0A091E]/50 border-white/20 text-white" data-testid="select-title">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1F1B71] border-white/10 text-white max-h-80">
                    {titles.map(t => (
                      <SelectItem key={t.tier} value={t.title} data-testid={`title-option-${t.tier}`}>
                        <span className="font-medium">{t.tier}. {t.title}</span>
                        <span className="text-white/50 text-xs ml-2">— {t.access_level_label}</span>
                      </SelectItem>
                    ))}
                    <SelectItem value={CLIENT_OPTION}>
                      <span className="font-medium">External Client</span>
                      <span className="text-white/50 text-xs ml-2">— Client portal access</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-white/40 text-xs">The title automatically dictates system permissions and access level.</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAddModal(false)} className="text-white/70">
              Cancel
            </Button>
            <Button 
              onClick={handleAddUser} 
              className="bg-[#D4AF37] text-[#0A091E]"
              data-testid="btn-submit-user"
            >
              Add Staff Member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
